/**
 * @module features/users/components/AdminDisableTotpModal
 * @description Administrative security modal allowing Super Admins to disable Two-Factor
 * Authentication for any user upon entering and verifying their own logged-in administrator password.
 */

import * as React from "react";
import {
  ShieldAlert,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  KeyRound,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "./UserAvatar";
import { UserDto } from "@/types/auth";
import { useDisableUserTotp } from "../api/users-api";
import { AppError } from "@/lib/api-client";

interface AdminDisableTotpModalProps {
  user: UserDto | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: (updatedUser: UserDto) => void;
}

export function AdminDisableTotpModal({
  user,
  open,
  onClose,
  onSuccess,
}: AdminDisableTotpModalProps) {
  const [adminPassword, setAdminPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const disableMutation = useDisableUserTotp();

  React.useEffect(() => {
    if (open) {
      setAdminPassword("");
      setShowPassword(false);
      setErrorMessage(null);
    }
  }, [open, user]);

  if (!user) return null;

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminPassword || disableMutation.isPending) return;

    setErrorMessage(null);
    try {
      const updatedUser = await disableMutation.mutateAsync({
        userId: user.id,
        adminPassword: adminPassword.trim(),
      });
      onSuccess?.(updatedUser);
      onClose();
    } catch (err: unknown) {
      if (err instanceof AppError) {
        setErrorMessage(
          err.errorDetails || err.message || "Failed to disable 2FA for this user."
        );
      } else {
        setErrorMessage("Failed to disable 2FA. Incorrect administrator password.");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md" sheetOnMobile>
        <DialogHeader>
          <div className="flex items-center gap-2.5 text-destructive">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <DialogTitle className="text-base font-semibold">
              Disable 2FA for User
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs pt-1">
            Administrative action to remove Two-Factor Authentication and invalidate all recovery codes.
          </DialogDescription>
        </DialogHeader>

        {/* Target User Summary Card */}
        <div className="p-3 rounded-xl border border-border/80 bg-muted/40 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <UserAvatar name={user.name} avatarUrl={user.avatar_url} size="sm" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">
                {user.name}
              </p>
              <p className="text-[11px] text-muted-foreground font-mono truncate">
                {user.email_id}
              </p>
            </div>
          </div>
          <span
            className={`text-[10px] font-mono font-medium px-2 py-0.5 rounded-full border shrink-0 ${
              user.role === "super_admin"
                ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 border-zinc-900 dark:border-zinc-200"
                : "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20"
            }`}
          >
            {user.role === "super_admin" ? "Admin" : "Client"}
          </span>
        </div>

        {errorMessage && (
          <div className="flex items-center gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleConfirm} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <label
              htmlFor="admin_confirm_password"
              className="text-xs font-medium text-foreground flex items-center gap-1.5"
            >
              <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Confirm Your Administrator Password</span>
            </label>
            <div className="relative">
              <Input
                id="admin_confirm_password"
                type={showPassword ? "text" : "password"}
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="Enter your logged-in password"
                autoComplete="current-password"
                className="h-10 text-sm font-mono pr-10"
                required
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                title={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              For security compliance, enter your current logged-in password to verify this administrative override.
            </p>
          </div>

          <DialogFooter className="pt-2 gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={disableMutation.isPending}
              className="h-9 text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={!adminPassword.trim() || disableMutation.isPending}
              className="h-9 text-xs min-w-[120px]"
            >
              {disableMutation.isPending ? (
                <div className="flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Verifying...</span>
                </div>
              ) : (
                "Verify & Disable 2FA"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
